import { inject, Injectable } from '@angular/core';
import { MenuType } from '../../models/menuType.enum';
import { Store } from '@ngrx/store';
import {
  selectCurrentLocationId, selectCurrentLogoSrc,
  selectCurrentMealId,
  selectCurrentMenuType,
  selectCurrentSelectedDate,
  selectPrimaryDishColor,
  selectAccentDishColor
} from './selectors/ui.selectors';
import {
  clearCurrentLocationId,
  clearCurrentMealId,
  clearCurrentMenuType,
  clearCurrentSelectedDate,
  setCurrentLocationId, setCurrentLogoSrc,
  setCurrentMealId,
  setCurrentMenuType,
  setCurrentSelectedDate,
  setPrimaryDishColor,
  setAccentDishColor
} from './actions/ui.actions';
import { Observable, combineLatest } from 'rxjs';
import {map, take, tap} from 'rxjs/operators';
import { endOfWeek, startOfWeek } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class UIService {
  private store = inject(Store);

  primaryDishColor$ = this.store.select(selectPrimaryDishColor);
  accentDishColor$ = this.store.select(selectAccentDishColor);
  currentLogoSrc$: Observable<string> = this.store.select(
    selectCurrentLogoSrc
  );
  currentSelectedDate$: Observable<Date> = this.store.select(
    selectCurrentSelectedDate
  );
  firstDayOfCurrentWeek$: Observable<Date | null> =
    this.currentSelectedDate$.pipe(
      map((date) => (!date ? null : startOfWeek(date)))
    );
  lastDayOfCurrentWeek$: Observable<Date | null> =
    this.currentSelectedDate$.pipe(
      map((date) => (!date ? null : endOfWeek(date)))
    );
  currentLocationId$: Observable<number | null> = this.store.select(
    selectCurrentLocationId
  );
  currentMealId$: Observable<number | null> =
    this.store.select(selectCurrentMealId);

  currentMenuType$: Observable<MenuType | null> = this.store.select(
    selectCurrentMenuType
  );

  watchAndUpdateColors$ = combineLatest([this.primaryDishColor$, this.accentDishColor$])
    .pipe(
      tap(([primaryDishColor, accentDishColor]) => {
        const style = document.createElement('style');
        style.textContent = `
          :root {
            --primary-dish-color: ${primaryDishColor};
            --accent-dish-color: ${accentDishColor};
          }
        `;

        // Remove any existing style elements we've created
        const existingStyles = document.head.getElementsByClassName('ui-service-styles');
        Array.from(existingStyles).forEach(el => el.remove());

        // Add our class to identify this style element
        style.className = 'ui-service-styles';
        document.head.appendChild(style);
      })
    );

  setCurrentSelectedDate(date: Date) {
    this.store.dispatch(setCurrentSelectedDate({ date }));
  }

  clearCurrentSelectedDate() {
    this.store.dispatch(clearCurrentSelectedDate());
  }

  setCurrentLocationId(locationId: number) {
    this.store.dispatch(setCurrentLocationId({ locationId }));
  }

  clearCurrentLocationId() {
    this.store.dispatch(clearCurrentLocationId());
  }

  setCurrentMealId(mealId: number) {
    this.store.dispatch(setCurrentMealId({ mealId }));
  }

  clearCurrentMealId() {
    this.store.dispatch(clearCurrentMealId());
  }

  setCurrentMenuType(menuType: MenuType) {
    this.store.dispatch(setCurrentMenuType({ menuType }));
  }

  clearCurrentMenuType() {
    this.store.dispatch(clearCurrentMenuType());
  }

  setLogoSrc(logoSrc: string) {
    this.store.dispatch(setCurrentLogoSrc({ logoSrc }));
  }

  showLoader(): void {
    document.body.className = 'modal-open';
    const element: HTMLElement = document.getElementById('app-loading');
    if (element) {
      element.style.display = 'flex';
    }
  }

  disableViewPicker(): void {
    (<HTMLInputElement>document.getElementById('week-toggle')).disabled = true;
    (<HTMLInputElement>document.getElementById('date-forward-btn')).disabled =
      true;
    (<HTMLInputElement>document.getElementById('date-back-btn')).disabled =
      true;
  }

  enableViewPicker(): void {
    (<HTMLInputElement>document.getElementById('week-toggle')).disabled = false;
    (<HTMLInputElement>document.getElementById('date-forward-btn')).disabled =
      false;
    (<HTMLInputElement>document.getElementById('date-back-btn')).disabled =
      false;
  }

  hideLoader() {
    const element = document.getElementById('app-loading');
    if (element) {
      element.style.display = 'none';
    }
    document.body.className = ' ';
  }

  setPrimaryDishColor(color: string) {
    const formattedColor = !color ? '#385F79' : color.startsWith('#') ? color : `#${color}`;
    this.store.dispatch(setPrimaryDishColor({ color: formattedColor }));
  }

  setAccentDishColor(color: string) {
    const formattedColor = !color ? '#506A23' : color.startsWith('#') ? color : `#${color}`;
    this.store.dispatch(setAccentDishColor({ color: formattedColor }));
  }
}

